import withStyles from 'isomorphic-style-loader/withStyles';
import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import s from './Popup.css';
import { graphql } from '../../post';
import {
  getFormatedValue,
  getHousingLabel,
  deviceTypeLabel,
  getDeviceLabel,
} from '@utils/utils';
import { usePopupLogic } from './Popup.functions';

/**
 * Composant Popup - Gestionnaire de popups modales
 *
 * @component
 * @param {Object} props - Props du composant
 * @param {Object} props.me - Données utilisateur
 * @param {Object} props.context - Contexte de l'application
 * @param {Function} props.t - Fonction de traduction
 * @returns {JSX.Element} Composant rendu
 */
const Popup = ({ me, context, t, ...props }) => {
  // Utilisation de la logique métier extraite
  const { state, handlers } = usePopupLogic({ me, context, t });

  // Si pas de popup à afficher, ne rien rendre
  if (!state.popup) {
    return null;
  }

  let content;

  switch (state.popup.type) {
    case 'customPopup':
      content = (
        <PopupCustom
          {...props}
          t={t}
          data={state.popup.data}
          onClose={handlers.closePopup}
        />
      );
      break;
    case 'leakagePopup':
      content = (
        <PopupLeakage
          {...props}
          t={t}
          data={state.popup.data}
          onClose={handlers.closePopup}
        />
      );
      break;
    case 'confirm':
      content = (
        <PopupConfirm
          {...props}
          t={t}
          data={state.popup.data}
          onClose={handlers.closePopup}
        />
      );
      break;
    default:
      content = (
        <PopupAlert
          {...props}
          t={t}
          data={state.popup.data}
          onClose={handlers.closePopup}
        />
      );
  }

  return content;
};

/**
 * Composant PopupCustom - Popup personnalisée avec contenu et boutons
 *
 * @component
 * @param {Object} props - Props du composant
 * @param {Object} props.data - Données de la popup
 * @param {Function} props.onClose - Fonction de fermeture
 * @param {Function} props.t - Fonction de traduction
 * @returns {JSX.Element} Composant rendu
 */
const PopupCustom = ({ data, onClose, t, ...props }) => {
  const handleValidate = useCallback(() => {
    onClose();
    if (data.handleValidate) {
      data.handleValidate();
    }
  }, [onClose, data]);

  useEffect(() => {
    return () => {
      if (data?.onClose) {
        data.onClose();
      }
    };
  }, [data]);

  return (
    <div className={s.popupLayer}>
      <div className={s.popupPanel}>
        <div className={s.popupBody}>{data.children}</div>
        <div className={s.popupFooter}>
          {data.actionButtons?.map((actionButton, index) => (
            <button
              key={`button_${index}`}
              onClick={actionButton.action || onClose}
            >
              {actionButton.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Composant PopupLeakage - Popup spécialisée pour les alarmes de fuite
 *
 * @component
 * @param {Object} props - Props du composant
 * @param {Object} props.data - Données de la popup de fuite
 * @param {Function} props.onClose - Fonction de fermeture
 * @param {Function} props.t - Fonction de traduction
 * @returns {JSX.Element} Composant rendu
 */
const PopupLeakage = ({ data, onClose, t, ...props }) => {
  return (
    <div className={s.popupLayer}>
      <div className={s.popupPanel}>
        <div className={s.popupBody}>
          <div className={s.popupLeakage}>
            <h2 className={[s.popupLeakageTitle, s.mb_xl].join(" ")}>
              {data.title}
            </h2>
            <div className={[s.popupLeakageText, s.mb_l].join(" ")}>
              {data.text}
            </div>
            {data.alarms?.map((alarm) => (
              <div
                key={`alarm_${alarm.id}`}
                className={[s.popupLeakageTextDevice, s.mb_s].join(" ")}
              >
                <span className={[s.pl_l].join(" ")}>
                  {alarm.jointHousingGroupLabel}
                </span>
                <span className={[s.pl_l].join(" ")}>
                  {alarm.address}
                </span>
                <span className={[s.pl_l].join(" ")}>
                  {alarm.housingLabel}
                </span>
                <span className={[s.pl_l].join(" ")}>
                  {alarm.deviceType}
                </span>
                <span className={[s.pl_l].join(" ")}>{alarm.deviceLabel}</span>
              </div>
            ))}
            {data.checkbox && (
              <div className={[s.mt_xl].join(" ")}>
              <label className={[s.flex_1].join(" ")}>
                <input
                  type="checkbox"
                  id="popupLeakage"
                  className={[s.popupLeakageCheckbox].join(" ")}
                  checked={data.checkbox.checked}
                  onChange={data.checkbox.onChange}
                />
                <span className={[s.ml_s].join(" ")}>
                  {data.checkbox.label}
                </span>
              </label>
            </div>
            )}
          </div>
        </div>
        <div className={s.popupFooter}>
          {data.actionButtons?.map((actionButton, index) => (
            <button
              key={`button_${index}`}
              onClick={actionButton.action || onClose}
            >
              {actionButton.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Composant PopupConfirm - Popup de confirmation avec boutons Annuler/Valider
 *
 * @component
 * @param {Object} props - Props du composant
 * @param {Object} props.data - Données de la popup
 * @param {Function} props.onClose - Fonction de fermeture
 * @param {Function} props.t - Fonction de traduction
 * @returns {JSX.Element} Composant rendu
 */
const PopupConfirm = ({ data, onClose, t, ...props }) => {
  const handleValidate = useCallback(() => {
    onClose();
    if (data.handleValidate) {
      data.handleValidate();
    }
  }, [onClose, data]);

  const handleCancel = useCallback(() => {
    onClose();
    if (data.handleCancel) {
      data.handleCancel();
    }
  }, [onClose, data]);

  return (
    <div className={s.popupLayer}>
      <div className={s.popupPanel}>
        <div className={s.popupBody} style={{ whiteSpace: 'pre-line' }}>{data.text}</div>
        <div className={s.popupFooter}>
          <button onClick={handleCancel}>{data.cancelButtonText}</button>
          <button onClick={handleValidate}>{data.validateButtonText}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Composant PopupAlert - Popup d'alerte avec bouton OK
 *
 * @component
 * @param {Object} props - Props du composant
 * @param {Object} props.data - Données de la popup
 * @param {Function} props.onClose - Fonction de fermeture
 * @param {Function} props.t - Fonction de traduction
 * @returns {JSX.Element} Composant rendu
 */
const PopupAlert = ({ data, onClose, t, ...props }) => {
  const handleValidate = useCallback(() => {
    onClose();
    if (data.handleValidate) {
      data.handleValidate();
    }
  }, [onClose, data]);

  const okButtonText = data?.okButtonText || t('global.ok');

  return (
    <div className={s.popupLayer}>
      <div className={s.popupPanel}>
        <div className={s.popupBody}>{data.text}</div>
        <div className={s.popupFooter}>
          <button onClick={handleValidate}>{okButtonText}</button>
        </div>
      </div>
    </div>
  );
};

// PropTypes pour le composant principal
Popup.propTypes = {
  me: PropTypes.shape({
    id: PropTypes.any,
    leakage_alarms: PropTypes.array,
  }),
  context: PropTypes.shape({
    fetch: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

// PropTypes pour les sous-composants
PopupCustom.propTypes = {
  data: PropTypes.shape({
    children: PropTypes.node,
    actionButtons: PropTypes.array,
    handleValidate: PropTypes.func,
    onClose: PropTypes.func,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

PopupConfirm.propTypes = {
  data: PropTypes.shape({
    text: PropTypes.string.isRequired,
    cancelButtonText: PropTypes.string.isRequired,
    validateButtonText: PropTypes.string.isRequired,
    handleValidate: PropTypes.func,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

PopupAlert.propTypes = {
  data: PropTypes.shape({
    text: PropTypes.string.isRequired,
    okButtonText: PropTypes.string,
    handleValidate: PropTypes.func,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(s)(Popup));
